import { useContext, useRef, useState } from 'react';

import { media } from 'context';
import { BLUE_500_HEX, GRAY_50_HEX } from 'shared';

import { AnyFilterOption, Column, DefaultFilterOptionConfig, FilterOption, FilterState, Row } from './types';
import Clickable from '../Clickable';
import Icon from '../Icon';
import Layout from '../Layout';
import Text from '../Text';
import { useOnClickOutside } from '../helpers';

interface FilterDropdownProps<T extends Row> {
  filterState: FilterState;
  setFilterState: (filterState: FilterState) => void;
  options: FilterOption[] | AnyFilterOption[];
  defaultOption: DefaultFilterOptionConfig;
  column: Column<T>;
}

const FilterDropdown = <T extends Row>({
  filterState,
  setFilterState,
  options,
  defaultOption,
  column
}: FilterDropdownProps<T>) => {
  const { isMobile } = useContext(media);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(containerRef, () => {
    setIsOpen(false);
  });

  const handleSelect = (option: number) => {
    setFilterState({ ...filterState, key: String(column.key), oneOf: [option] });
    setIsOpen(false);
  };

  const handleSelectAny = () => {
    setFilterState({ ...filterState, key: String(column.key), oneOf: [] });
    setIsOpen(false);
  };

  const isDefaultAny = defaultOption.any;

  const selectedOption = isDefaultAny
    ? options.find(o => 'id' in o && o.id === filterState.oneOf?.[0]) || defaultOption.anyFilterOption
    : options.find(o => 'id' in o && o.id === filterState.oneOf?.[0]) || defaultOption.option;

  const menuOptions =
    selectedOption === defaultOption.anyFilterOption
      ? options
      : [
          ...(defaultOption.anyFilterOption ? [defaultOption.anyFilterOption] : []),
          ...options.filter(o => 'id' in o && selectedOption && 'id' in selectedOption && o.id !== selectedOption.id)
        ];

  return (
    <Layout __ref={containerRef} position="relative" width={isMobile ? '100%' : 'fit-content'} borderRadius={8}>
      <button
        style={{
          paddingLeft: isOpen ? 10 : 12,
          paddingRight: 12,
          height: isMobile ? '48px' : '32px',
          borderRadius: '8px',
          border: isOpen ? `2px solid ${BLUE_500_HEX}` : `1px solid ${GRAY_50_HEX}`,
          width: isMobile ? '100%' : undefined
        }}
        onClick={() => setIsOpen(!isOpen)}
        className="hover-bg-color-gray-50"
      >
        <Layout
          direction="row"
          align="center"
          gap={isMobile ? 8 : undefined}
          justify={isMobile ? 'space-between' : undefined}
        >
          <Text size="body-sm" wrap={false}>
            {selectedOption?.label}
          </Text>
          <Icon name="downCaret" size="xs" />
        </Layout>
      </button>
      {isOpen && (
        <Layout
          position="absolute"
          color="white"
          paddingY={8}
          boxShadow="0px 8px 24px 0px rgba(0, 0, 0, 0.12)"
          borderRadius={8}
          width={isMobile ? '100%' : 132}
          top="100%"
          marginTop={4}
          zIndex={2000}
          maxHeight={200}
          overflowY="auto"
        >
          {menuOptions.map(option => (
            <Clickable
              key={option.label}
              onClick={() => {
                if ('any' in option && option.any) {
                  handleSelectAny();
                } else if ('id' in option) {
                  handleSelect(option.id ?? 0);
                }
              }}
              fullWidth
            >
              <Layout paddingY={8} paddingX={16} hoverColor="gray-50" height={isMobile ? 48 : 32} zIndex={1000}>
                <Text size="body-xs" align="left" wrap={false}>
                  {option.label}
                </Text>
              </Layout>
            </Clickable>
          ))}
        </Layout>
      )}
    </Layout>
  );
};

export default FilterDropdown;
